/**
 * ReviewModal — shown when user completes their first pipeline.
 * Awards 5 free credits for leaving a review.
 */

import { useState } from 'react';
import { api } from '../api/client';
import { useCreditsStore } from '../store/creditsStore';

interface Props {
  pipelineId: string;
  onClose: () => void;
}

export default function ReviewModal({ pipelineId, onClose }: Props) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [creditsAwarded, setCreditsAwarded] = useState(0);
  const [error, setError] = useState('');
  const { fetchBalance } = useCreditsStore();

  const handleSubmit = async () => {
    if (rating < 1) return;
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post('/reviews', { pipelineId, rating, text });
      setCreditsAwarded(data.creditsAwarded || 0);
      setSubmitted(true);
      // Refresh credit balance
      fetchBalance();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError('You already reviewed this pipeline');
      } else {
        setError('Failed to submit review. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#13141A', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 24, padding: '40px 32px', maxWidth: 480, width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
      >
        {submitted ? (
          /* ── Success state ── */
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h3 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 8 }}>
              Thank you!
            </h3>
            {creditsAwarded > 0 ? (
              <div style={{
                background: 'rgba(0,230,118,0.08)', border: '1px solid rgba(0,230,118,0.25)',
                borderRadius: 12, padding: '16px 24px', marginBottom: 24,
              }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: '#00e676', marginBottom: 4 }}>
                  +{creditsAwarded} credits
                </div>
                <p style={{ fontSize: 13, color: '#6b7280' }}>
                  Added to your balance — use them on your next pipeline!
                </p>
              </div>
            ) : (
              <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>
                Your review helps us improve StartupForge for everyone.
              </p>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '12px 32px', borderRadius: 12,
                background: '#f97316', border: 'none',
                color: '#fff', fontSize: 15, fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Continue
            </button>
          </div>
        ) : (
          /* ── Review form ── */
          <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>⭐</div>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 8 }}>
                How was your experience?
              </h3>
              <p style={{ fontSize: 14, color: '#6b7280' }}>
                Leave a review and earn <span style={{ color: '#00e676', fontWeight: 700 }}>5 free credits</span>
              </p>
            </div>

            {/* Star rating */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 32, transition: 'transform 0.1s',
                    transform: (hoverRating || rating) >= star ? 'scale(1.1)' : 'scale(1)',
                    filter: (hoverRating || rating) >= star ? 'none' : 'grayscale(1) opacity(0.3)',
                  }}
                >
                  ★
                </button>
              ))}
            </div>

            {/* Text feedback */}
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Tell us what you liked or what we can improve... (optional)"
              rows={3}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 12,
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff', fontSize: 14, resize: 'vertical',
                fontFamily: 'inherit', marginBottom: 16,
              }}
            />

            {error && (
              <p style={{ fontSize: 13, color: '#ef4444', marginBottom: 12, textAlign: 'center' }}>{error}</p>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 12,
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#6b7280', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Skip
              </button>
              <button
                onClick={handleSubmit}
                disabled={rating < 1 || submitting}
                style={{
                  flex: 2, padding: '12px 0', borderRadius: 12,
                  background: rating >= 1 ? '#f97316' : 'rgba(255,255,255,0.06)',
                  border: 'none',
                  color: '#fff', fontSize: 14, fontWeight: 700, cursor: rating >= 1 ? 'pointer' : 'not-allowed',
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? 'Submitting...' : 'Submit & Earn 5 Credits'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
